import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { InputSize } from '../../../enum/input-size.enum';
import {
  Editor,
  NgxEditorComponent,
  NgxEditorConfig,
  NgxEditorMenuComponent,
  NgxEditorService,
  Toolbar,
} from 'ngx-editor';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

const customEditorConfig: NgxEditorConfig = {
  icons: {
    bold: '<svg xmlns="http://www.w3.org/2000/svg" width="9" height="12" viewBox="0 0 9 12" fill="none"><path d="M0 11.375V0H4.19604C5.07382 0 5.86639 0.273612 6.57375 0.820834C7.28097 1.36806 7.63458 2.10569 7.63458 3.03375C7.63458 3.65653 7.48764 4.17118 7.19375 4.57771C6.9 4.98424 6.57951 5.2834 6.23229 5.47521C6.67354 5.6359 7.08514 5.9409 7.46708 6.39021C7.84903 6.83951 8.04 7.43271 8.04 8.16979C8.04 9.26271 7.63327 10.0701 6.81979 10.5921C6.00632 11.114 5.18875 11.375 4.36708 11.375H0ZM1.91667 9.58979H4.28979C4.95021 9.58979 5.41264 9.40764 5.67708 9.04333C5.94153 8.67903 6.07375 8.32965 6.07375 7.99521C6.07375 7.66076 5.93806 7.31139 5.66667 6.94708C5.39528 6.58278 4.91875 6.40063 4.23708 6.40063H1.91667V9.58979ZM1.91667 4.70833H4.07854C4.58493 4.70833 4.98986 4.56007 5.29333 4.26354C5.59667 3.96715 5.74833 3.62396 5.74833 3.23396C5.74833 2.80451 5.59083 2.45354 5.27583 2.18104C4.9607 1.90868 4.57438 1.7725 4.11688 1.7725H1.91667V4.70833Z" fill="currentColor"/></svg>',
    italic:
      '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none"><path d="M0 11.375V9.95188H3.18104L5.96479 1.42313H2.70042V0H10.4952V1.42313H7.40375L4.62021 9.95188H7.79479V11.375H0Z" fill="currentColor"/></svg>',
    underline:
      '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="13" viewBox="0 0 10 13" fill="none"><path d="M0 12.9263V11.8429H9.42292V12.9263H0ZM4.71146 10.2627C3.48493 10.2627 2.5325 9.89431 1.85417 9.1575C1.17569 8.4207 0.836458 7.43278 0.836458 6.19375V0H2.26083V6.26958C2.26083 7.06764 2.47368 7.70674 2.89937 8.18688C3.32521 8.66702 3.92944 8.90708 4.71208 8.90708C5.49486 8.90708 6.09903 8.66702 6.52458 8.18688C6.95028 7.70674 7.16313 7.06764 7.16313 6.26958V0H8.58646V6.19375C8.58646 7.43278 8.24722 8.4207 7.56875 9.1575C6.89042 9.89431 5.93799 10.2627 4.71146 10.2627Z" fill="currentColor"/></svg>',
    strike:
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none"><path d="M7.91667 11.455C6.99153 11.455 6.18597 11.1981 5.5 10.6842C4.81417 10.1703 4.3516 9.49292 4.11229 8.65208L5.30292 8.18104C5.5625 8.8734 5.91347 9.39479 6.35583 9.74521C6.79806 10.0956 7.32688 10.2708 7.94229 10.2708C8.63146 10.2708 9.19743 10.0975 9.64021 9.75083C10.0831 9.40403 10.3046 8.93257 10.3046 8.33646C10.3046 8.05007 10.2615 7.8025 10.1754 7.59375C10.0894 7.38486 9.95514 7.18799 9.7725 7.00313H11.2933C11.3947 7.14521 11.4665 7.32306 11.5087 7.53667C11.551 7.75042 11.5721 8.01007 11.5721 8.31563C11.5721 9.23549 11.2324 9.98868 10.5529 10.5752C9.87333 11.1617 8.99458 11.455 7.91667 11.455ZM0 5.50313V4.41979H15.5833V5.50313H0ZM7.79167 0C8.59514 0 9.24979 0.154097 9.75563 0.462292C10.2615 0.770486 10.6949 1.2675 11.056 1.95333L9.93583 2.44542C9.74042 2.03944 9.44958 1.71736 9.06333 1.47917C8.67708 1.24083 8.26174 1.12167 7.81729 1.12167C7.15924 1.12167 6.63333 1.30146 6.23958 1.66104C5.84583 2.02049 5.66444 2.44007 5.69542 2.91979H4.42792C4.39694 2.09826 4.70306 1.40625 5.34625 0.84375C5.98931 0.28125 6.80444 0 7.79167 0Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    bullet_list:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none"><path d="M4.33979 10.2612V9.17792H13.1667V10.2612H4.33979ZM4.33979 6.01125V4.92792H13.1667V6.01125H4.33979ZM4.33979 1.76125V0.677916H13.1667V1.76125H4.33979ZM1.21521 10.9392C0.878542 10.9392 0.591875 10.8193 0.355208 10.5796C0.118403 10.3399 0 10.0517 0 9.71521C0 9.37854 0.119861 9.09188 0.359583 8.85521C0.599305 8.6184 0.887431 8.5 1.22396 8.5C1.56063 8.5 1.84729 8.61986 2.08396 8.85958C2.32076 9.09931 2.43917 9.38743 2.43917 9.72396C2.43917 10.0606 2.31931 10.3473 2.07958 10.584C1.83986 10.8208 1.55174 10.9392 1.21521 10.9392ZM1.21521 6.68917C0.878542 6.68917 0.591875 6.56931 0.355208 6.32958C0.118403 6.08986 0 5.80174 0 5.46521C0 5.12854 0.119861 4.84188 0.359583 4.60521C0.599305 4.3684 0.887431 4.25 1.22396 4.25C1.56063 4.25 1.84729 4.36986 2.08396 4.60958C2.32076 4.8493 2.43917 5.13743 2.43917 5.47396C2.43917 5.81062 2.31931 6.09729 2.07958 6.33396C1.83986 6.57076 1.55174 6.68917 1.21521 6.68917ZM1.21521 2.43917C0.878542 2.43917 0.591875 2.31931 0.355208 2.07958C0.118403 1.83986 0 1.55174 0 1.21521C0 0.878541 0.119861 0.591875 0.359583 0.355208C0.599305 0.118403 0.887431 0 1.22396 0C1.56063 0 1.84729 0.11986 2.08396 0.359582C2.32076 0.599304 2.43917 0.887431 2.43917 1.22396C2.43917 1.56063 2.31931 1.84729 2.07958 2.08396C1.83986 2.32076 1.55174 2.43917 1.21521 2.43917Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ordered_list:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0 13.1667V12.4231H2V11.6667H1V10.9231H2V10.1667H0V9.42313H2.37188C2.47715 9.42313 2.56542 9.45875 2.63667 9.53C2.70792 9.60125 2.74354 9.68951 2.74354 9.79479V10.9552C2.74354 11.0605 2.70792 11.1488 2.63667 11.22C2.56542 11.2913 2.47715 11.3269 2.37188 11.3269C2.47715 11.3269 2.56542 11.3625 2.63667 11.4338C2.70792 11.505 2.74354 11.5933 2.74354 11.6987V12.7948C2.74354 12.9002 2.70792 12.9885 2.63667 13.0598C2.56542 13.131 2.47715 13.1667 2.37188 13.1667H0ZM0 8.45521V6.58333C0 6.47806 0.0356251 6.38972 0.106875 6.31833C0.178125 6.24708 0.266458 6.21146 0.371875 6.21146H2V5.45521H0V4.71146H2.37188C2.47715 4.71146 2.56542 4.74708 2.63667 4.81833C2.70792 4.88972 2.74354 4.97806 2.74354 5.08333V6.58333C2.74354 6.68861 2.70792 6.77694 2.63667 6.84833C2.56542 6.91958 2.47715 6.95521 2.37188 6.95521H0.743542V7.71146H2.74354V8.45521H0ZM1 3.74354V0.743542H0V0H1.74354V3.74354H1ZM4.33979 11.375V10.2917H13.1667V11.375H4.33979ZM4.33979 7.125V6.04167H13.1667V7.125H4.33979ZM4.33979 2.875V1.79167H13.1667V2.875H4.33979Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    align_left:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0 13.1667V12.0833H13.1667V13.1667H0ZM0 10.1458V9.0625H9.16667V10.1458H0ZM0 7.125V6.04167H13.1667V7.125H0ZM0 4.10417V3.02083H9.16667V4.10417H0ZM0 1.08333V0H13.1667V1.08333H0Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    align_center:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0 13.1667V12.0833H13.1667V13.1667H0ZM3 10.1458V9.0625H10.1667V10.1458H3ZM0 7.125V6.04167H13.1667V7.125H0ZM3 4.10417V3.02083H10.1667V4.10417H3ZM0 1.08333V0H13.1667V1.08333H0Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    align_right:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0 1.08333V0H13.1667V1.08333H0ZM4 4.10417V3.02083H13.1667V4.10417H4ZM0 7.125V6.04167H13.1667V7.125H0ZM4 10.1458V9.0625H13.1667V10.1458H4ZM0 13.1667V12.0833H13.1667V13.1667H0Z" fill="currentColor"/></svg>',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    align_justify:
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0 13.1667V12.0833H13.1667V13.1667H0ZM0 10.1458V9.0625H13.1667V10.1458H0ZM0 7.125V6.04167H13.1667V7.125H0ZM0 4.10417V3.02083H13.1667V4.10417H0ZM0 1.08333V0H13.1667V1.08333H0Z" fill="currentColor"/></svg>',
  },
};

@Component({
  selector: 'lib-rich-text-input',
  imports: [ReactiveFormsModule, FormsModule, NgxEditorMenuComponent, NgxEditorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextInputComponent),
      multi: true,
    },
    NgxEditorService,
  ],
  templateUrl: './rich-text-input.component.html',
  styleUrl: './rich-text-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextInputComponent implements ControlValueAccessor {
  private editorService = inject(NgxEditorService);
  private destroyRef = inject(DestroyRef);

  placeholder = input<string>('');
  error = input<boolean>(false);
  valueChange = output<string | null>();

  protected readonly InputSize = InputSize;

  public value = signal<string | null>(null);
  public disabled = signal(false);

  protected readonly editor = new Editor({
    content: '',
    plugins: [],
    nodeViews: {},
    history: true,
    keyboardShortcuts: true,
    inputRules: true,
  });
  toolbar: Toolbar = [
    // default value
    ['bold', 'italic', 'underline', 'strike'],
    ['bullet_list', 'ordered_list'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  constructor() {
    this.editorService.config = {
      ...this.editorService.config,
      icons: {
        ...this.editorService.config?.icons,
        ...customEditorConfig.icons,
      },
    };

    this.destroyRef.onDestroy(() => {
      this.editor.destroy();
    });
  }

  public writeValue(value: string | null): void {
    this.value.set(value);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected handleInput(text: string) {
    this.value.set(text);
    this.valueChange.emit(text);
    this.onChange(text);
  }

  private onChange: (value: string | null) => void = () => {
    // default onChange
  };

  private onTouched: () => void = () => {
    // default onTouched
  };
}
